import React, { useMemo, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Container, Row, Col, OverlayTrigger, Popover } from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";

import TOTLogo from ".//./../../../assets/brand/ColorLogo_ImageOnly.png";

import { MembershipPlansEmojiPicker } from "../../../components/membership/MembershipPlans-EmojiPicker.js";
import "../../../components/membership/MembershipPlans-ActiveSubHeader.css";
import {
    PLAN_ID,
    getPlanTitle,
    useMembershipSubscriptionStatus,
} from "../../../components/membership/MembershipPlans-SubscriptionStatus.js";

const logoStyle = {
    height: "40px",
    width: "60%",
    color: "transparent",
    cursor: "pointer",
    marginTop: "8px",
    marginBottom: "8px",
};

const headerBadgeCssOverride = `
.hm-mc-badge{
    display: inline-block;
    width: auto;
    max-width: none;
}

.hm-mc-badge .mc-current-sub{
    display: inline-flex;
    width: auto;
    max-width: none;
    min-width: 0;
    background: #1a2a3a;
    border-color: rgba(255,255,255,.10);
}

.hm-mc-badge .mc-current-sub-inner{
    display: inline-flex;
    width: auto;
    max-width: none;
    min-width: 0;
}

.hm-mc-badge .mc-current-sub-text{
    min-width: 0;
    flex: 0 1 auto;
}

.hm-mc-badge .mc-current-sub-name{
    white-space: nowrap;
    overflow: visible;
    text-overflow: clip;
    color: #e8f0f8;
}

.hm-mc-badge .mc-current-sub-title{
    color: #7a9ab8;
}

.hm-mc-badge .mc-current-sub:hover{ transform: none; }
`;

const membershipBenefitsBlackGold = (
    <Popover id="popover-basic">
        <Popover.Header as="h3" style={{ backgroundColor: "#C8B568", color: "#000" }}>
            <p style={{ fontSize: ".85rem", fontWeight: "700", textAlign: "center", padding: "0px", margin: "0px" }}>Current Membership Benefits</p>
        </Popover.Header>
        <Popover.Body style={{ padding: "10px" }}>
            <ul style={{ fontSize: ".75rem", padding: "0px", margin: "0px", paddingLeft: "10px" }}>
                <li>Unlimited PDF XLSX/CDEX data, 1099s</li>
            </ul>
        </Popover.Body>
    </Popover>
);

function CurrentSubscriptionBadge({ status, planId, onClick, showText = true }) {
    const rawMemberTypeLabel = (status?.memberTypeLabel || "").toString() || (status?.kind === "paid" ? "Paid Member" : status?.kind === "trial" ? "Trial" : "Free Member");
    const hidePaidLabel = planId === PLAN_ID.blackgoldRevenue || planId === PLAN_ID.blackgoldJib || planId === PLAN_ID.oilBaron || planId === PLAN_ID.bigRig;
    const memberTypeLabel = hidePaidLabel ? "" : rawMemberTypeLabel;
    const planTitle = (status?.planLabel || getPlanTitle(planId) || "").toString();
    const trialEndMs = Number(status?.trialEndMs || 0);
    const trialDaysRemaining = status?.isTrialActive && trialEndMs > 0 ? Math.max(0, Math.ceil((trialEndMs - Date.now()) / 86400000)) : 0;
    const planNameText = status?.isTrialActive && trialDaysRemaining > 0 ? `${(planTitle || "").toUpperCase()} (${trialDaysRemaining} DAYS REMAINING)` : (planTitle || "").toUpperCase();
    const planText = memberTypeLabel ? `${planNameText} | ${memberTypeLabel}` : planNameText;
    const isWildcatterCustom = planId === PLAN_ID.wildcatter;

    const textFadeStyle = {
        opacity: showText ? 1 : 0,
        transition: "opacity 0.4s ease",
    };

    const badgeStyle = {
        minWidth: "240px",
    };

    return (
        <div
            className="mc-current-sub"
            aria-label="Current subscription"
            role="button"
            tabIndex={0}
            onClick={onClick}
            style={badgeStyle}
            onKeyDown={(e) => {
                if (!onClick) return;
                if (e.key === "Enter" || e.key === " ") onClick(e);
            }}
        >
            <div className="mc-current-sub-inner">
                <MembershipPlansEmojiPicker
                    planId={planId}
                    isWildcatterCustom={isWildcatterCustom}
                    as="div"
                    className="mc-current-sub-icon"
                    emojiClassName="mc-current-sub-emoji"
                />

                <div className="mc-current-sub-text" style={textFadeStyle}>
                    <div className="mc-current-sub-title">Current subscription</div>
                    <div className="mc-current-sub-name">{planText}</div>
                </div>

                <img className="mc-current-sub-logo" src={TOTLogo} alt="totdata.com" style={textFadeStyle} />
            </div>
        </div>
    );
}

function LegacyMembershipBanner({ subscriptionStatus }) {
    const planLabel = (subscriptionStatus?.planLabel || "BlackGold (Legacy)").toString();

    return (
        <OverlayTrigger placement="right" overlay={membershipBenefitsBlackGold}>
            <div style={{ marginTop: "5px", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: "700", fontSize: ".8rem", cursor: "pointer" }}>
                <span style={{ margin: "0px", padding: "0px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Membership status:&nbsp;&nbsp;</span>
                <span style={{ color: "#C8B568" }}>{planLabel}</span>
                <p style={{ margin: "0px", padding: "0px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <span style={{ margin: "0px", padding: "0px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", visibility: "hidden" }}>Member since:&nbsp;&nbsp;8/05/2024</span>
                </p>
            </div>
        </OverlayTrigger>
    );
}

export default function HeaderMembership(props) {
    const navigate = useNavigate();
    const outletContext = useOutletContext();
    const { getAccessTokenSilently } = useAuth0();

    const outletCurrentAccount = Array.isArray(outletContext) ? outletContext[0] : null;
    const outletAccountProfile = Array.isArray(outletContext) ? outletContext[1] : null;
    const outletBaseUrl = Array.isArray(outletContext) ? outletContext[5] : null;

    const propCurrentAccount = props?.currentAccount ?? null;
    const propAccountProfile = props?.accountProfile ?? null;
    const baseUrl = props?.baseUrl ?? outletBaseUrl ?? "";
    const currentAccount = useMemo(() => ({
        ...(outletCurrentAccount || {}),
        ...(propCurrentAccount || {}),
    }), [outletCurrentAccount, propCurrentAccount]);

    const accountProfile = useMemo(() => ({
        ...(outletAccountProfile || {}),
        ...(propAccountProfile || {}),
    }), [outletAccountProfile, propAccountProfile]);

    const {
        resolvedAccountId,
        subscriptionsLoading,
        subscriptionStatusReady,
        subscriptionStatus,
        effectivePlanId,
        reloadSubscriptions,
    } = useMembershipSubscriptionStatus({
        accountId: currentAccount?.id ?? currentAccount?.Id ?? accountProfile?.accountId ?? accountProfile?.AccountId ?? 0,
        currentAccount,
        accountProfile,
        getAccessTokenSilently,
        includeCancelable: false,
        baseUrl
    });

    // Subscription data is fetched once on mount by the hook.
    // Manual reloads (e.g. after Stripe webhook callbacks) use reloadSubscriptions().
    // We intentionally do NOT re-fetch on focus/visibilitychange to avoid the
    // "flash to Wildcatters" when switching browser tabs.

    const hasResolvedDisplayStatus = !resolvedAccountId || subscriptionStatusReady;
    const shouldUseLegacyBanner = hasResolvedDisplayStatus && Boolean(subscriptionStatus?.isBlackGoldLegacy);
    const shouldUseNewBadge = hasResolvedDisplayStatus && !shouldUseLegacyBanner;
    const planId = effectivePlanId || PLAN_ID.wildcatter;

    // Cache the last successfully resolved display values so that during
    // re-fetches the header keeps showing the previous plan instead of
    // reverting to Wildcatters.
    const cachedDisplayRef = useRef(null);

    if (subscriptionStatusReady && resolvedAccountId) {
        cachedDisplayRef.current = {
            planId,
            status: subscriptionStatus,
            shouldUseLegacyBanner,
            shouldUseNewBadge,
        };
    }

    const containerStyle = {
        border: "none",
        background: shouldUseLegacyBanner ? "#000" : "transparent",
        borderRadius: shouldUseLegacyBanner ? "5px" : "0px",
        paddingLeft: "0px",
        marginTop: "-8px",
        marginBottom: shouldUseLegacyBanner ? "-8px" : "0px",
        paddingBottom: shouldUseLegacyBanner ? "0px" : "10px",
    };

    const isStillLoading = resolvedAccountId && !subscriptionStatusReady;
    const cached = cachedDisplayRef.current;

    // While loading, prefer the cached values over the wildcatter fallback.
    const displayPlanId = isStillLoading ? (cached?.planId ?? PLAN_ID.wildcatter) : planId;
    const displayStatus = isStillLoading ? (cached?.status ?? null) : subscriptionStatus;
    const displayShouldUseLegacyBanner = isStillLoading ? (cached?.shouldUseLegacyBanner ?? false) : shouldUseLegacyBanner;
    const displayShouldUseNewBadge = isStillLoading ? (cached?.shouldUseNewBadge ?? true) : shouldUseNewBadge;

    const displayContainerStyle = {
        ...containerStyle,
        background: displayShouldUseLegacyBanner ? "#000" : "transparent",
        borderRadius: displayShouldUseLegacyBanner ? "5px" : "0px",
        marginBottom: displayShouldUseLegacyBanner ? "-8px" : "0px",
        paddingBottom: displayShouldUseLegacyBanner ? "0px" : "10px",
    };

    return (
        <Container style={displayContainerStyle}>
            <Row>
                <Col xs={4} style={{ padding: "0px" }}>
                    {(() => {
                        if (displayShouldUseLegacyBanner) {
                            return <LegacyMembershipBanner subscriptionStatus={displayStatus} />;
                        }

                        if (displayShouldUseNewBadge) {
                            return (
                                <div className="hm-mc-badge" style={{ marginTop: "0px", paddingTop: "10px" }}>
                                    <style>{`\n${headerBadgeCssOverride}`}</style>
                                    <CurrentSubscriptionBadge status={displayStatus} planId={displayPlanId} showText={subscriptionStatusReady} onClick={() => navigate("/membership-plans")} />
                                </div>
                            );
                        }

                        return null;
                    })()}
                </Col>

                <Col xs={4} style={{ marginBottom: "0px", fontWeight: "700", fontSize: ".75rem", textAlign: "center", color: displayShouldUseNewBadge ? "#000" : "#fff", paddingTop: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayShouldUseLegacyBanner ? <img src={TOTLogo} alt="totdata.com" style={logoStyle} onClick={() => navigate("/")} /> : null}
                </Col>

                <Col xs={4} style={{ marginTop: "4px" }} />
            </Row>
        </Container>
    );
} 